import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { student_routes } from "../../../router/all_routes";
import "./index.css";
import { getClassExamPlanningById } from "../../../../api/planningExam";
import { toast } from "sonner";

interface Exam {
  title: string;
  className: string;
  date: string;
  time: string;
  duration: string;
  room: string;
  building: string;
  instructions: string[];
  supervisors: string[];
  countdownDays: number;
}

const examData: Exam = {
  title: "Mathématiques",
  className: "Licence 2 — Informatique",
  date: "Mardi 18 Mars 2026",
  time: "08:00 - 10:00",
  duration: "2 heures",
  room: "Salle B20",
  building: "Bâtiment B, 2eme etage",
  instructions: [
    "Durée de l'examen : 2 heures.",
    "Venez muni de votre carte étudiante.",
    "Calculatrice scientifique autorisée.",
    "Téléphones portables éteints et rangés.",
    "Documents et notes personnelles interdits.",
  ],
  supervisors: ["Pr. KONE Mamadou", "M. TRAORE Ibrahim"],
  countdownDays: 3,
};

const StudentExamDetails = () => {
  const routes = student_routes;
  const location = useLocation();
  const periodIdFromUrl = new URLSearchParams(location.search).get("periodId");
  const myExamsUrl = periodIdFromUrl
    ? `${routes.myExams}?periodId=${periodIdFromUrl}`
    : routes.myExams;

  const { id } = useParams(); // On récupère l'id de l'URL

  const [examDetail, setExamDetail] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(true);
  const [isClassesOpen, setIsClassesOpen] = useState(true);

  const fetchExamDetails = async () => {
    try {
      setIsLoading(true);

      const response = await getClassExamPlanningById(id);
      const examData = response.data || [];

      setExamDetail(examData);

      console.log("Exam", response);
    } catch (error: any) {
      console.log("erreur de chargement du Cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les salles en cas d'erreur
      setExamDetail(null);
    } finally {
      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    }
  };

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!id) return;

    fetchExamDetails();
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);

    return formatted.replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getExamDuration = (start?: string, end?: string) => {
    if (!start || !end) return "-";

    const [startHour, startMinute] = start.split(":").map(Number);
    const [endHour, endMinute] = end.split(":").map(Number);

    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    const diffMinutes = endTotalMinutes - startTotalMinutes;

    if (diffMinutes <= 0) return "-";

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}min`;
    }

    if (hours > 0) {
      return `${hours}h`;
    }

    return `${minutes}min`;
  };

  const getExamStatus = (dateExam?: string, start?: string, end?: string) => {
    if (!dateExam || !start || !end) {
      return {
        label: "-",
        icon: "ti ti-clock-hour-4",
        bg: "#64748b",
      };
    }

    const now = new Date();
    const startDate = new Date(`${dateExam}T${start}`);
    const endDate = new Date(`${dateExam}T${end}`);

    if (now > endDate) {
      return {
        label: "Termine",
        icon: "ti ti-check",
        bg: "#22c55e",
      };
    }

    if (now >= startDate && now <= endDate) {
      return {
        label: "Aujourd'hui",
        icon: "ti ti-clock-hour-4",
        bg: "#f59e0b",
      };
    }

    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const startOfExamDay = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
    );
    const diffDays = Math.round(
      (startOfExamDay.getTime() - startOfToday.getTime()) /
        (1000 * 60 * 60 * 24),
    );

    if (diffDays === 0) {
      return {
        label: "Aujourd'hui",
        icon: "ti ti-calendar-event",
        bg: "#f59e0b",
      };
    }

    if (diffDays === 1) {
      return {
        label: "Demain",
        icon: "ti ti-calendar-time",
        bg: "#e67e16",
      };
    }

    return {
      label: `Dans ${diffDays} jours`,
      icon: "ti ti-calendar-event",
      bg: "#e67e16",
    };
  };

  const examStatus = getExamStatus(
    examDetail?.date_exam,
    examDetail?.sessionStart,
    examDetail?.sessionEnd,
  );

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="text-center">
              <span className="spinner-border text-primary" role="status" />
              <p className="mt-2 mb-0 text-muted">Chargement des détails...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!examDetail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="text-center py-5 text-muted">
            <p className="mb-3">Impossible de charger cet examen.</p>

            <Link to={myExamsUrl} className="btn btn-primary">
              Retour aux examens
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Détails de l'examen</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={myExamsUrl}>Mes examens</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {examDetail?.Subject?.label || "Détails de l'examen"}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="examd-banner d-sm-flex align-items-center justify-content-between p-3 mb-3 rounded">
          <div className="d-flex align-items-center mb-2 mb-sm-0 w-100">
            <div className="avatar avatar-lg flex-shrink-0 me-3 d-flex align-items-center justify-content-center examd-banner-icon">
              <i className="ti ti-book-2 fs-24" />
            </div>

            <div className="w-100">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                <p className="mb-0 text-white-50 fs-12 text-uppercase">
                  {examDetail?.type || "Evaluation"}{" "}
                  {examDetail?.practical ? "Pratique" : "Théorique"}
                </p>
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="badge rounded-pill examd-status-badge"
                    style={{ backgroundColor: examStatus.bg }}
                  >
                    <i className={`${examStatus.icon} me-1`} />
                    {examStatus.label}
                  </span>
                </div>
              </div>

              <h5 className="mb-1 text-white fw-semibold">
                {examDetail?.Subject?.label || "Détails de l'examen"}
              </h5>
              <p className="mb-0 text-white-50">
                {examDetail?.Class?.Level?.abbreviation} -{" "}
                {examDetail?.Class?.label}
              </p>
            </div>
          </div>
        </div>

        <div className="examd-card mb-3">
          <div className="examd-info-row pb-2 pt-2">
            <div className="examd-info-icon examd-info-red">
              <i className="ti ti-calendar-event" />
            </div>
            <div>
              <p className="examd-label">Date</p>
              <p className="examd-value">{formatDate(examDetail?.date_exam)}</p>
            </div>
          </div>

          <div className="examd-info-row pb-2 pt-2">
            <div className="examd-info-icon examd-info-orange">
              <i className="ti ti-clock-hour-4" />
            </div>
            <div>
              <p className="examd-label">Horaire</p>
              <p className="examd-value">
                {examDetail?.sessionStart} - {examDetail?.sessionEnd}
              </p>
              <p className="examd-sub">
                Durée :{" "}
                {getExamDuration(
                  examDetail?.sessionStart,
                  examDetail?.sessionEnd,
                )}
              </p>
            </div>
          </div>

          <div className="examd-info-row pb-2 pt-2">
            <div className="examd-info-icon examd-info-blue">
              <i className="ti ti-map-pin" />
            </div>
            <div>
              <p className="examd-label">Lieu</p>
              <p className="examd-value">{examDetail?.Classroom?.label}</p>
            </div>
          </div>
        </div>

        <div className="examd-card mb-3">
          <button
            type="button"
            className="examd-section-head examd-section-head-button"
            onClick={() => setIsInstructionsOpen((prev) => !prev)}
          >
            <h5>
              <i className="ti ti-clipboard-text" />
              Consignes de l'examen
            </h5>
            <i
              className={`ti ${isInstructionsOpen ? "ti-chevron-up" : "ti-chevron-down"}`}
            />
          </button>
          {isInstructionsOpen &&
            ((examDetail?.Consignes?.length ?? 0) > 0 ? (
              examDetail?.Consignes.map((consigne: any, index: any) => (
                <div key={index} className="examd-list-row">
                  <span className="examd-check">
                    <i className="ti ti-check" />
                  </span>
                  <p>{consigne.general_instruction}</p>
                </div>
              ))
            ) : (
              <div className="examd-empty-state">
                <div className="examd-empty-icon">
                  <i className="ti ti-clipboard-off" />
                </div>
                <p>Aucune consigne particulière pour cet examen.</p>
              </div>
            ))}
        </div>

        <div className="examd-card mb-3">
          <button
            type="button"
            className="examd-section-head examd-section-head-button"
            onClick={() => setIsClassesOpen((prev) => !prev)}
          >
            <h5>
              <i className="ti ti-school" />
              Classes en commun pour cet examen
            </h5>
            <i
              className={`ti ${isClassesOpen ? "ti-chevron-up" : "ti-chevron-down"}`}
            />
          </button>
          {isClassesOpen &&
            ((examDetail?.common_classes?.length ?? 0) > 0 ? (
              examDetail?.common_classes.map((classe: any) => (
                <div key={classe.id} className="examd-list-row">
                  <span className="examd-check">
                    <i className="ti ti-school" />
                  </span>
                  <p>
                    {classe?.Level?.abbreviation} {classe?.abbreviation}
                  </p>
                </div>
              ))
            ) : (
              <div className="examd-empty-state">
                <div className="examd-empty-icon">
                  <i className="ti ti-school-off" />
                </div>
                <p>Aucune autre classe en commun pour cette épreuve.</p>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default StudentExamDetails;
